import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LogoMark } from "./TopBar";

const ICONS = {
  home: (
    <path d="M3 11.5 12 4l9 7.5M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" />
  ),
  persona: <><circle cx="12" cy="8" r="4" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  vault: <path d="M4 4h16v16H4zM4 9h16M9 4v16" />,
  resume: <path d="M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6" />,
  match: <path d="M17 20a4 4 0 1 0-8 0M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM3 20a4 4 0 0 1 4-4M21 20a4 4 0 0 0-4-4" />,
  interview: <path d="M21 11.5a8.5 8.5 0 1 1-4-7.2L21 3l-1 4.8a8.4 8.4 0 0 1 1 3.7Z" />,
  reports: <path d="M4 4h16v16H4zM8 16V12M12 16V8M16 16v-5" />,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" /></>,
  company: <path d="M3 21V7l9-4 9 4v14M3 10h18M9 21v-4h6v4" />,
} as const;

function SideIcon({ name, active }: { name: keyof typeof ICONS; active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke={active ? "#0EA5E9" : "#94A3B8"} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {ICONS[name]}
    </svg>
  );
}

type NavItem = { to: string; label: string; icon: keyof typeof ICONS; end?: boolean; section?: string };

const TALENT_ITEMS: NavItem[] = [
  { to: "/app", label: "Home", icon: "home", end: true },
  { to: "/app/persona", label: "My Persona", icon: "persona" },
  { to: "/app/resume", label: "AI Resume", icon: "resume" },
  { to: "/app/matches", label: "Team Match", icon: "match" },
  { to: "/app/interview", label: "AI Interview", icon: "interview" },
  { to: "/app/settings", label: "Settings", icon: "settings", section: "ACCOUNT" },
];

const COMPANY_ITEMS: NavItem[] = [
  { to: "/company", label: "Dashboard", icon: "home", end: true },
  { to: "/company/culture", label: "Team Culture", icon: "persona" },
  { to: "/company/talent", label: "Find Talent", icon: "search" },
  { to: "/company/interview", label: "Interviews", icon: "interview" },
  { to: "/app/settings", label: "Settings", icon: "settings", section: "ACCOUNT" },
];

export function Sidebar() {
  const { user } = useAuth();
  const isCompany = user?.role === "company";
  const items = isCompany ? COMPANY_ITEMS : TALENT_ITEMS;

  let lastSection: string | undefined;

  return (
    <aside className="hidden lg:flex lg:w-[220px] xl:w-[240px] flex-col border-r border-slate-100 bg-white">
      {/* Logo */}
      <div className="flex items-center gap-2 px-5 py-5">
        <LogoMark className="h-7 w-7 rounded-md object-contain" />
        <span className="text-base font-bold tracking-tight text-navy">personaw</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 px-3 pt-2">
        {items.map((item) => {
          const showSection = item.section && item.section !== lastSection;
          if (item.section) lastSection = item.section;

          return (
            <div key={item.to}>
              {showSection && (
                <p className="mb-2 mt-6 px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {item.section}
                </p>
              )}
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
                    isActive
                      ? "bg-teal/10 text-teal-dark font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-navy"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <SideIcon name={item.icon} active={isActive} />
                    {item.label}
                  </>
                )}
              </NavLink>
            </div>
          );
        })}
      </nav>

      {/* Bottom CTA */}
      <div className="p-4">
        <div className="rounded-2xl bg-gradient-to-br from-navy to-navy-light p-4 text-white">
          <p className="text-xs font-semibold">
            {isCompany ? "Better Teams, Greater Impact" : "Ready for your next opportunity?"}
          </p>
          <p className="mt-1 text-[11px] text-white/60">
            {isCompany ? "Find the right people for your team culture." : "Let AI find the best team for you."}
          </p>
          <NavLink
            to={isCompany ? "/company/talent" : "/app/matches"}
            className="mt-3 inline-flex items-center rounded-full bg-teal px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-teal-dark"
          >
            {isCompany ? "Search Talent →" : "Explore Matches →"}
          </NavLink>
        </div>
      </div>
    </aside>
  );
}
